/**
 * Program budget by category (2026-10-01). Categories are whatever the
 * program uses (vendors, people, licences, contingency…), entered by hand —
 * the old endpoint invented an even five-way split. Remaining is computed.
 */
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { Button } from '../ui/button'
import { programBudgetService, type ProgramBudgetCategory } from '../../services/programBudgets'
import { apiErrorMessage } from '../../utils/apiError'

const money = (v: number) => v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const input = 'h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground'

interface Props { programId: number; programBudget: number; canEdit: boolean }

export function BudgetCategories({ programId, programBudget, canEdit }: Props) {
  const [rows, setRows] = useState<ProgramBudgetCategory[] | null>(null)
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [draft, setDraft] = useState({ category: '', allocated: '', spent: '' })
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    programBudgetService.listCategories(programId).then(setRows).catch(() => setRows([]))
  }, [programId])
  useEffect(() => { load() }, [load])

  const start = (row?: ProgramBudgetCategory) => {
    setEditing(row ? row.id : 'new')
    setDraft(row ? { category: row.category, allocated: String(Number(row.allocated)), spent: String(Number(row.spent)) }
      : { category: '', allocated: '', spent: '' })
  }

  const save = async () => {
    const body = { category: draft.category.trim(), allocated: Number(draft.allocated || 0), spent: Number(draft.spent || 0) }
    if (!body.category || body.allocated < 0 || body.spent < 0) {
      toast.error('Give the category a name and non-negative amounts.')
      return
    }
    setBusy(true)
    try {
      if (editing === 'new') await programBudgetService.addCategory(programId, body)
      else if (editing !== null) await programBudgetService.updateCategory(programId, editing, body)
      setEditing(null)
      load()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the category.'))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (row: ProgramBudgetCategory) => {
    if (!window.confirm(`Delete the “${row.category}” category?`)) return
    try {
      await programBudgetService.deleteCategory(programId, row.id)
      load()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not delete the category.'))
    }
  }

  const list = rows ?? []
  const allocated = list.reduce((s, r) => s + Number(r.allocated), 0)
  const spent = list.reduce((s, r) => s + Number(r.spent), 0)
  const unallocated = programBudget - allocated

  const editRow = (key: string | number) => (
    <tr key={key} className="border-b border-border">
      <td className="px-3 py-2"><input aria-label="Category" className={`${input} w-full`} value={draft.category} maxLength={50}
        onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="e.g. Vendors" /></td>
      <td className="px-3 py-2"><input aria-label="Allocated" type="number" min={0} className={`${input} w-32`} value={draft.allocated}
        onChange={(e) => setDraft({ ...draft, allocated: e.target.value })} /></td>
      <td className="px-3 py-2"><input aria-label="Spent" type="number" min={0} className={`${input} w-32`} value={draft.spent}
        onChange={(e) => setDraft({ ...draft, spent: e.target.value })} /></td>
      <td className="px-3 py-2 text-muted-foreground">—</td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}>Save</Button>
        </div>
      </td>
    </tr>
  )

  return (
    <section aria-label="Budget by category" className="rounded-lg border border-border bg-card p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg text-foreground">Budget by category</h3>
        {canEdit && editing === null && (
          <Button variant="outline" onClick={() => start()} className="gap-2"><Plus className="h-4 w-4" /> Add category</Button>
        )}
      </div>
      {rows === null ? <p className="text-sm text-muted-foreground">Loading…</p> : list.length === 0 && editing !== 'new' ? (
        <p className="text-sm text-muted-foreground">
          No categories yet. Split the program budget the way you report it — vendors, people, licences, contingency.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2">Category</th><th className="px-3 py-2">Allocated</th><th className="px-3 py-2">Spent</th>
                <th className="px-3 py-2">Remaining</th><th className="px-3 py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => editing === r.id ? editRow(r.id) : (
                <tr key={r.id} className="border-b border-border">
                  <td className="px-3 py-2 text-foreground">{r.category}</td>
                  <td className="px-3 py-2 text-muted-foreground">{money(Number(r.allocated))}</td>
                  <td className="px-3 py-2 text-muted-foreground">{money(Number(r.spent))}</td>
                  <td className={`px-3 py-2 ${Number(r.remaining) < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>{money(Number(r.remaining))}</td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && editing === null && (
                      <div className="flex justify-end gap-1">
                        <button type="button" aria-label={`Edit ${r.category}`} onClick={() => start(r)} className="rounded p-1 text-muted-foreground hover:bg-muted"><Pencil className="h-4 w-4" /></button>
                        <button type="button" aria-label={`Delete ${r.category}`} onClick={() => remove(r)} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {editing === 'new' && editRow('new')}
            </tbody>
            {list.length > 0 && (
              <tfoot>
                <tr className="text-foreground">
                  <td className="px-3 py-2 font-medium">Total</td>
                  <td className="px-3 py-2 font-medium">{money(allocated)}</td>
                  <td className="px-3 py-2 font-medium">{money(spent)}</td>
                  <td className="px-3 py-2 font-medium">{money(allocated - spent)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
          {programBudget > 0 && list.length > 0 && (
            <p className={`mt-2 text-xs ${unallocated < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {unallocated >= 0 ? `${money(unallocated)} of the ${money(programBudget)} program budget isn’t assigned to a category yet.`
                : `Categories add up to ${money(-unallocated)} more than the ${money(programBudget)} program budget.`}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
